import { useState } from 'react';
import { api, errorText } from '../lib/api';
import { classNames, fmtTime, KIND_LABEL } from '../lib/util';

/** Nonblocking questions: ambiguities, contradictions and edit conflicts. */
export function Clarifications({ items, campaignId, onChanged, offline }: { items: any[]; campaignId: string; onChanged: () => void; offline: boolean }) {
  const open = items.filter((c) => c.status === 'open');
  const recent = items.filter((c) => c.status !== 'open');
  return (
    <div className="clarifications">
      {open.length === 0 && <p className="muted empty-small">No open questions. Keep playing.</p>}
      {open.map((c) => (
        <Item key={c.id} c={c} campaignId={campaignId} onChanged={onChanged} offline={offline} />
      ))}
      {recent.length > 0 && (
        <details className="recent">
          <summary>Recently resolved ({recent.length})</summary>
          {recent.map((c) => (
            <Item key={c.id} c={c} campaignId={campaignId} onChanged={onChanged} offline={offline} />
          ))}
        </details>
      )}
    </div>
  );
}

function Item({ c, campaignId, onChanged, offline }: { c: any; campaignId: string; onChanged: () => void; offline: boolean }) {
  const [answer, setAnswer] = useState('');
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr('');
    try {
      await fn();
      onChanged();
    } catch (x) {
      setErr(errorText(x));
    } finally {
      setBusy(false);
    }
  };
  const answerWith = (a: string) => run(() => api(`/api/campaigns/${campaignId}/clarifications/${c.id}/answer`, { body: { answer: a } }));
  const status = (s: string) => run(() => api(`/api/campaigns/${campaignId}/clarifications/${c.id}/status`, { body: { status: s } }));
  const disabled = busy || offline;

  return (
    <article className={classNames('clar', `clar-${c.kind}`, c.status !== 'open' && 'clar-closed')}>
      <p className="clar-q">
        <span className="tag">{c.kind === 'contradiction' ? 'Contradiction' : c.kind === 'edit_conflict' ? 'Edit conflict' : 'Question'}</span> {c.question}
      </p>
      {c.entries?.length > 0 && c.kind !== 'edit_conflict' && (
        <ul className="sources">
          {c.entries.map((e: any) => (
            <li key={e.id}>
              <time className="muted small">{fmtTime(e.captured_at)}</time> {e.text}
            </li>
          ))}
        </ul>
      )}

      {c.kind === 'contradiction' && (
        <div className="claims-vs">
          {c.claims.map((cl: any, i: number) => (
            <div key={cl.id} className="claim-box">
              <p className="small muted">{i === 0 ? 'Earlier record' : 'Newer note'} · {KIND_LABEL[cl.kind] ?? cl.kind} · {cl.status}</p>
              <p>{cl.statement}</p>
              {cl.sources.map((s: any) => (
                <p key={s.entryId} className="small source-line">
                  Session {s.sessionNumber ?? '?'}, {fmtTime(s.capturedAt)}: “{s.text}”
                </p>
              ))}
            </div>
          ))}
        </div>
      )}

      {c.kind === 'edit_conflict' && (
        <div className="claims-vs">
          {c.revisions.map((r: any) => (
            <div key={r.id} className="claim-box">
              <p className="small muted">
                {r.status === 'conflict' ? 'Other device' : 'Current'} · {fmtTime(r.created_at)}
              </p>
              <p>{r.text}</p>
              {c.status === 'open' && (
                <button className="btn small" disabled={disabled} onClick={() => void run(() => api(`/api/entries/${c.entries[0].id}/resolve-conflict`, { body: { revisionId: r.id } }))}>
                  Keep this version
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {c.status === 'open' && c.kind === 'ambiguity' && (
        <div className="clar-answer">
          {c.options?.length > 0 && (
            <div className="row wrap">
              {c.options.map((o: string) => (
                <button key={o} className="btn small" disabled={disabled} onClick={() => void answerWith(o)}>
                  {o}
                </button>
              ))}
            </div>
          )}
          <form
            className="row"
            onSubmit={(e) => {
              e.preventDefault();
              if (answer.trim()) void answerWith(answer.trim());
            }}
          >
            <input value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="Your answer" aria-label="Answer" disabled={disabled} />
            <button className="btn primary small" disabled={disabled || !answer.trim()}>
              Answer
            </button>
          </form>
          <button className="link small" disabled={disabled} onClick={() => void status('dismissed')}>
            Not needed
          </button>
        </div>
      )}

      {c.status === 'open' && c.kind === 'contradiction' && (
        <div className="clar-answer">
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note about the resolution" aria-label="Resolution note" disabled={disabled} />
          <div className="row wrap">
            {[
              ['keep_existing', 'Keep earlier'],
              ['accept_new', 'Accept newer'],
              ['both_true', 'Both true'],
              ['dismiss', 'Not a conflict'],
            ].map(([k, label]) => (
              <button key={k} className="btn small" disabled={disabled} onClick={() => void run(() => api(`/api/campaigns/${campaignId}/clarifications/${c.id}/resolve`, { body: { choice: k, note: note || undefined } }))}>
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {c.status !== 'open' && (
        <p className="small muted">
          {c.status === 'auto_resolved' ? 'Answered by later notes' : c.status === 'dismissed' ? 'Dismissed' : 'Answered'}
          {c.answer ? `: ${c.answer}` : ''}{' '}
          {c.kind === 'ambiguity' && (
            <button className="link small" disabled={disabled} onClick={() => void status('open')}>
              Reopen
            </button>
          )}
        </p>
      )}
      {offline && c.status === 'open' && <p className="muted small">Answering needs a server connection.</p>}
      {err && <p className="error small">{err}</p>}
    </article>
  );
}
